import {
  DayCellContentArg,
  EventContentArg,
  SlotLabelContentArg
} from '@fullcalendar/core'
import { Box, IconButton, Typography, useMediaQuery } from '@mui/material'
import { AddButton } from 'assets/icons'
import CircleContainer from 'components/containers/CircleContainer'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import {
  CALENDAR_VIEW,
  useCalendar
} from 'components/providers/CalendarProvider'
import { isToday } from 'date-fns'
import dayjs from 'dayjs'
import i18n from 'i18n'
import { t } from 'i18next'
import { getDefaultTimezone } from 'index'
import { useEffect, useState, memo } from 'react'
import { RemoveTempEvents } from 'services/db/Queries/Event'
import LocalStorage from 'services/LocalStorage'
import { theme } from 'theme'
import { EventDoc, GroupDoc, UserSettingsDoc } from 'types/api/changes'
import { CalendarRange } from 'types/components/calendar'
import { LocalStorageKey } from 'types/services/localStorage'
import { isUserHaveGroupToAddEvents } from 'utils/api/groups'
import { isGroupcalApp } from 'utils/appType'
import { APP_COLOR } from 'utils/colors'
import { isMobileDevice } from 'utils/constants'
import {
  generateStartEndBasedOnTimeWithShift,
  isAMPM,
  StartEndEvent
} from 'utils/date'
import { isGuest } from 'utils/user'

import Event from './Event'
import * as Styled from './styled'
import Database from 'services/db/Database'
import { eq } from 'lodash'

const weekHeaderFontSize = 14

export interface DayHeaderContentProps {
  dayNameFormat?: string
  userSettings?: UserSettingsDoc
  groupId?: string
  groups?: GroupDoc[]
  handleAddEvent?: (startEnd: StartEndEvent) => void
  date: Date
  group?: GroupDoc
}

export function DayHeaderContent(props: DayHeaderContentProps) {
  const textColor = theme.palette.text.primary
  const calendar = useCalendar()
  const dayNameTextColor = theme.palette.text.disabled

  const dateForHeader = dayjs(props.date).toDate()
  const text = dayjs(dateForHeader).format('D')
  const today = isToday(dateForHeader)

  const [_, setHaveActiveCalendar] = useState(false)
  const [showEventAddingNotPossible, onShowEventAddingNotPossible] =
    useState(false)

  const isLargeScreen = useMediaQuery(theme.breakpoints.down('lg'))

  useEffect(() => {
    isUserHaveGroupToAddEvents().then((groupId) => {
      if (groupId) {
        setHaveActiveCalendar(true)
      } else {
        setHaveActiveCalendar(false)
      }
    })
  }, [props.groups])

  switch (calendar.view) {
    case CalendarRange.LISTDAY:
    case CalendarRange.LISTWEEK:
      return (
        <Box
          display={'flex'}
          flexDirection={'row'}
          height={'20px'}
          width={'100%'}
          gap={1}
          alignItems={'center'}
          position={'relative'}
        >
          {today && (
            <Box display={'flex'} flexDirection={'row'}>
              <Typography
                fontWeight={550}
                color={isGroupcalApp() ? 'white' : 'black'}
              >
                {t('today')}
              </Typography>
              <Typography
                className="day-name"
                color={isGroupcalApp() ? 'white' : 'black'}
              >
                {`${today ? ', ' : ''}`}
              </Typography>
            </Box>
          )}
          <Typography
            className="day-name"
            color={isGroupcalApp() && today ? 'white' : undefined}
          >
            {`${dayjs(dateForHeader).format(props.dayNameFormat ?? 'dddd')}, `}
          </Typography>
          <Typography
            className="day-number"
            color={isGroupcalApp() && today ? 'white' : undefined}
          >
            {dayjs(dateForHeader).format('MMM DD YYYY')}
          </Typography>
          <Box
            position={'absolute'}
            right={i18n.dir() === 'ltr' ? -6 : undefined}
            left={i18n.dir() === 'rtl' ? -6 : undefined}
          >
            {isGuest() ? (
              <></>
            ) : (
              <IconButton
                onClick={() => {
                  const now = dayjs()
                  const startEnd = generateStartEndBasedOnTimeWithShift(
                    dayjs(dateForHeader).set('hour', now.hour()).unix()
                  )
                  if (props.handleAddEvent) props.handleAddEvent(startEnd)
                }}
              >
                <AddButton width={'24px'} height={'24px'} />
              </IconButton>
            )}
          </Box>
          {showEventAddingNotPossible && (
            <ConfirmationDialog
              title={''}
              content={t('toAddEvents')}
              leftButtonHidden
              rightButton={`${t('ok')}`}
              rightButtonColor={APP_COLOR}
              handleRightButton={() => {
                onShowEventAddingNotPossible(false)
              }}
              open
              handleClose={() => {}}
            />
          )}
        </Box>
      )

    case CalendarRange.WEEK:
      return (
        <Box
          onClick={() => {
            RemoveTempEvents()
          }}
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <CircleContainer
            color={isToday(dateForHeader) ? APP_COLOR : undefined}
          >
            <div
              style={{
                width: '100%',
                fontWeight: '500',
                padding: theme.spacing(0, 1),
                marginRight: text.length > 1 ? '1px' : undefined,
                color:
                  isGroupcalApp() && isToday(dateForHeader)
                    ? 'white'
                    : textColor
              }}
            >
              {text}
            </div>
          </CircleContainer>
          <Typography fontSize={weekHeaderFontSize} color={dayNameTextColor}>
            {dayjs(dateForHeader)
              .tz(getDefaultTimezone())
              .format(
                props.dayNameFormat ?? (isMobileDevice() ? 'ddd' : 'dddd')
              )}
          </Typography>
        </Box>
      )
    case CalendarRange.DAY:
      return (
        <Styled.DayHeaderContentDay gap={2}>
          <CircleContainer
            color={isToday(dateForHeader) ? APP_COLOR : undefined}
          >
            <Typography
              fontSize={24}
              p={1}
              color={
                isGroupcalApp() && isToday(dateForHeader) ? 'white' : textColor
              }
            >
              {dayjs(dateForHeader).format('D')}
            </Typography>
          </CircleContainer>

          <Typography fontSize={24} color={textColor}>
            {dayjs(dateForHeader).format(props.dayNameFormat ?? 'dddd')}
          </Typography>
        </Styled.DayHeaderContentDay>
      )
    default: {
      const now = dayjs()
      const header = dayjs(dateForHeader).utc()
      const visible = dayjs(
        Number(LocalStorage.get(LocalStorageKey.VISIBLE_DATE))
      )

      const isCurrentMonth =
        now.month() === visible.month() && now.year() === visible.year()
      const isSameDay = now.day() === header.day()
      return (
        <Typography
          width={'100%'}
          fontSize={18}
          color={
            isCurrentMonth && isSameDay ? APP_COLOR : theme.palette.text.primary
          }
        >
          {header.format(
            props.dayNameFormat ??
              (isMobileDevice() || isLargeScreen ? 'ddd' : 'dddd')
          )}
        </Typography>
      )
    }
  }
}

export function DayCellContent(props: DayCellContentArg) {
  const bgColor = isToday(props.date) ? APP_COLOR : undefined
  switch (props.view.type) {
    case CALENDAR_VIEW[CalendarRange.WEEK]:
      return null
    case CALENDAR_VIEW[CalendarRange.DAY]:
      return null
    default:
      return (
        <div style={{ marginTop: 2, marginLeft: 1, marginRight: 1 }}>
          <CircleContainer color={bgColor}>
            <Typography
              fontSize={'16px'}
              width={'100%'}
              minWidth={'24px'}
              textAlign={'center'}
              color={isToday(props.date) && isGroupcalApp() ? 'white' : 'black'}
            >
              {props.dayNumberText}
            </Typography>
          </CircleContainer>
        </div>
      )
  }
}

interface EventContentProps extends EventContentArg {
  closeAll?: boolean // for the drag/resize callbacks when we want to hide all opened popups
  ignoreDim?: boolean
  embedMode?: boolean
}

// Custom comparison function to perform deeper equality check on the important props
const arePropsEqual = (
  prevProps: EventContentProps,
  nextProps: EventContentProps
) => {
  // Compare basic properties
  if (
    prevProps.ignoreDim !== nextProps.ignoreDim ||
    prevProps.isStart !== nextProps.isStart ||
    prevProps.closeAll !== nextProps.closeAll ||
    prevProps.embedMode !== nextProps.embedMode
  ) {
    return false
  }

  // Compare event data
  const prevEvent = prevProps.event
  const nextEvent = nextProps.event

  if (!prevEvent || !nextEvent) return prevEvent === nextEvent

  const equal =
    prevEvent.startStr === nextEvent.startStr &&
    prevEvent.endStr === nextEvent.endStr &&
    prevEvent.allDay === nextEvent.allDay &&
    prevEvent._def.title === nextEvent._def.title &&
    prevEvent._def.publicId === nextEvent._def.publicId &&
    JSON.stringify(
      Database.toServerObject(prevEvent._def.extendedProps?.eventModel)
    ) ===
      JSON.stringify(
        Database.toServerObject(nextEvent._def.extendedProps?.eventModel)
      )

  return equal
}

export function EventContent(props: EventContentProps) {
  const event: EventDoc = props.event?._def?.extendedProps?.eventModel

  let instanceStart = dayjs(props.event.startStr).valueOf()
  let instanceEnd = dayjs(props.event.endStr).valueOf()

  if (props.event.allDay) {
    instanceStart = dayjs.utc(props.event.startStr).startOf('day').valueOf()

    /**
     * because of mismatch between formats of all day events, we need to *remove* extra day for our data source, in order to show
     * events correctly on a screen. The reason for that is that for example event which goes from October 9 to Octover 10
     * will be saved in our system as "from 2024-10-09 to 2024-10-10", when FullCalendar and some other providers support
     * format as such: "from 2024-10-09 to 2024-10-11", meaning, last day of the long event is day after */

    instanceEnd = dayjs
      .utc(
        props.event.endStr.length > 0
          ? props.event.endStr
          : props.event.startStr
      )
      .startOf('day')
      .subtract(
        event.StartDate !== event.EndDate ||
          (event.Recurrence && typeof event.Recurrence != 'string')
          ? 1
          : 0,
        'day'
      )
      .valueOf()

    /**
     * In case of all day event without a timezon (iOS bug) we do not need to subtract a day
     */

    if (event.TimeZoneNameID?.length === 0) {
      instanceEnd = dayjs
        .utc(
          props.event.endStr.length > 0
            ? props.event.endStr
            : props.event.startStr
        )
        .startOf('day')
        .valueOf()
    }
  }

  return (
    <Event
      ignoreDim={props.ignoreDim}
      isStart={props.isStart}
      instanceStart={instanceStart}
      instanceEnd={instanceEnd}
      event={props.event?._def?.extendedProps?.eventModel}
      title={props.event._def.title}
      closeAll={props.closeAll}
      fc_id={props.event._def.publicId}
      embedMode={props.embedMode}
    />
  )
}

export function AllDayContent() {
  return <Styled.SlotContent>{`${i18n.t('allDay')}`}</Styled.SlotContent>
}

export function SlotLabelContent(props: SlotLabelContentArg) {
  const time = dayjs(props.date).tz(getDefaultTimezone())
  return (
    <Styled.SlotContent data-color="secondary" data-time>
      {time.hour() == 0 && time.minute() == 0
        ? ''
        : dateToTimeString(dayjs(props.date).tz(getDefaultTimezone()).toDate())}
    </Styled.SlotContent>
  )
}

export default function dateToTimeString(
  dateObj: Date,
  keepMinutes?: boolean
): string {
  const date = dayjs(dateObj).tz(getDefaultTimezone())

  let formatString = 'h A'

  if (
    keepMinutes ||
    (!date.format('A').toLowerCase().includes('am') &&
      !date.format('A').toLowerCase().includes('pm'))
  ) {
    formatString = 'h:mm A'
  }

  if (!isAMPM()) {
    formatString = 'HH:mm'
  }

  const timeString = date.format(formatString)

  return timeString.charAt(0) === '0' && timeString.length >= 5
    ? timeString.slice(1, timeString.length)
    : timeString
}
